// frameStream.ts (CX1) - the ordered per-ACTION frame stream a hand plays out as, and the projection that
// re-expresses the per-street `FeltView` as a function of one frame (so no existing mode breaks).
//
// This is the spine of Phase 11 (the cinematic table). Today the UI binds to `FeltView` - ONE flattened
// snapshot per street - which structurally cannot express "five bets sitting out front, then a sweep, then
// the pot jumps". The engine already produces the right thing: `advance(state, action)` returns a complete
// `HandState` after EVERY action. CX1 turns a played hand into that ordered sequence of frames, plus the
// two beats the engine never emits as data: the SWEEP at a street's close (chips fly in, the pot jumps) and
// the AWARD/push at showdown (the pot goes to the winner). See docs/ui-explorations/CX0_CINEMATIC_TABLE_SPEC.md.
//
// HOW IT WORKS (pure wiring over existing engine internals - NOT new strategy): a hand is fully determined
// by its true initial `HandState` (createHand output: blinds posted, empty history) plus its ordered list of
// actions. We REPLAY those actions through the shipped `advance()` and capture a frame after each. The
// terminal `advance()` call bundles the closing action + showdown + the pot push all at once, so we SPLIT it
// into two synthesized frames (sweep then award) per CX0 §1.3.
import { advance } from '@postflop/engine';
import type { Action, CardIndex, HandState } from '@postflop/schema';
import { type FeltView, type SeatView, ringFor, money } from './bridge';

// ── the frame contract (CX0 §1.2) ──────────────────────────────────────────────────────────────────────

export type FrameActionKind = 'fold' | 'check' | 'call' | 'bet' | 'raise' | 'jam';
export type FrameStreet = 'preflop' | 'flop' | 'turn' | 'river';

/** 'acting' = a normal action beat. 'sweep' = this frame closes the street (the previous frame's fronts fly
 *  to center and the pot jumps). 'award' = the synthesized terminal push. A frame is acting|sweep on the way
 *  through a street, or the one award frame at the very end. (Board-deal is not a phase - a frame whose
 *  `board` is longer than the previous frame's IS the deal beat; CX2 reads that.) */
export type FramePhase = 'acting' | 'sweep' | 'award';

/** The action that PRODUCED this frame. null only on the initial pre-first-action frame and the award frame. */
export interface FrameAction {
  seat: number;
  kind: FrameActionKind;
  /** ALWAYS whole dollars (the villain.ts float bug is fixed at source). Aggressive actions (bet/raise/jam)
   *  surface the raise-TO total - the number they made it, which equals the seat's `front`. A `call` surfaces
   *  what it COST to call (the to-call delta, capped by the caller's stack for a short all-in call) - how a
   *  call is actually spoken ("he called 36"). fold/check are 0. */
  amount: number;
}

/** One seat AS OF this frame - a projection of engine `PlayerState`: numbers, never display strings. */
export interface FrameSeat {
  seat: number; // engine seat id = HandState.players[].seat - the join key to the persistent pod
  stack: number; // chips behind, whole dollars
  front: number; // committedThisStreet - chips out in front this street (0 after a sweep)
  folded: boolean;
  allIn: boolean;
}

export interface HandFrame {
  i: number; // ordinal in the stream
  street: FrameStreet;
  phase: FramePhase;
  board: CardIndex[]; // 0 / 3 / 4 / 5 cards; longer than the previous frame's board = a deal beat
  /** the pot the felt SHOWS = sum(pots) - sum(fronts). Settled chips only; this street's fronts are NOT in it
   *  yet (that is the whole "pot-before / pot-after" point - raw engine `pots` already includes the fronts,
   *  so the subtraction is required). On the synthesized sweep/award frames it is set explicitly. */
  settledPot: number;
  toAct: number | null; // seat to act; null = street closed / hand over
  action: FrameAction | null;
  /** this frame closes the street: the PREVIOUS frame's fronts sweep to center and settledPot jumps. */
  sweep: boolean;
  seats: FrameSeat[];
}

/** The synthesized terminal frame the engine NEVER emits (advance() silently pushes the pot into the winner's
 *  stack and empties `pots` inside the closing action - CX0 §1.3). `settledPot` is 0 here (the pot is gone);
 *  the push it represents is `award.amount`, moving from the preceding sweep frame's center to the winner(s). */
export interface AwardFrame extends HandFrame {
  phase: 'award';
  award: {
    winners: number[]; // seats that won a (side) pot
    amount: number; // total pushed = the final pot
    shown: { seat: number; cards: readonly [CardIndex, CardIndex] }[]; // revealed at showdown (empty if uncontested)
  };
}

export interface HandStream {
  handId: string;
  seed?: string;
  stakes: { sb: number; bb: number; ante?: number };
  buttonSeat: number;
  heroSeat: number;
  heroCards: readonly [CardIndex, CardIndex];
  /** engine seat -> the pod's stable label. Name / read-tag / avatar live on the SESSION (persistent across
   *  hands, TB3); the stream carries only the join key + a fallback label. */
  seats: { seat: number; label: string }[];
  frames: HandFrame[]; // the last element is the AwardFrame
}

/** The metadata the stream carries beyond the played frames - everything not derivable from the HandState. */
export interface StreamMeta {
  handId: string;
  seed?: string;
  heroSeat: number;
  heroCards: readonly [CardIndex, CardIndex];
  seats: { seat: number; label: string }[];
}

// ── derivations from a HandState ─────────────────────────────────────────────────────────────────────────

/** The settled pot the felt shows: all pots minus this street's fronts (which are visually still out front,
 *  not yet in the pot). Raw engine `pots` INCLUDES the fronts, so the subtraction is required (POC-verified). */
const settledPotOf = (s: HandState): number =>
  s.pots.reduce((sum, p) => sum + p.amount, 0) - s.players.reduce((sum, p) => sum + p.committedThisStreet, 0);

/** Project the engine players into the frame's per-seat rows. */
const frameSeatsOf = (players: readonly HandState['players'][number][]): FrameSeat[] =>
  players.map((p) => ({ seat: p.seat, stack: p.stack, front: p.committedThisStreet, folded: p.folded, allIn: p.allIn }));

/** The FrameAction for `action`, computed from the PRE-action state (so a call's cost is exact even though
 *  the closing call's `committedThisStreet` gets reset by beginStreet before we could read it back). */
const frameActionOf = (action: Action, pre: HandState): FrameAction => {
  const actor = pre.players.find((p) => p.seat === action.seat);
  if (!actor) throw new Error(`frameActionOf: no player at seat ${action.seat}`);
  let amount: number;
  switch (action.kind) {
    case 'fold':
    case 'check':
      amount = 0;
      break;
    case 'call':
      // what it COST to call = the to-call delta, capped by the caller's stack (a short all-in call pays only
      // what it has). currentBet - committed in the normal case; = frame 9's $36 in the POC (called 36 to make it 39).
      amount = Math.min(pre.currentBet, actor.committedThisStreet + actor.stack) - actor.committedThisStreet;
      break;
    default:
      // bet / raise / jam: the raise-TO total the engine normalized into history (equals the seat's front).
      amount = action.amount ?? 0;
      break;
  }
  return { seat: action.seat, kind: action.kind, amount };
};

// ── the stream builder ───────────────────────────────────────────────────────────────────────────────────

/**
 * Replay a played hand into its ordered frame stream. `initial` is the true pre-first-action HandState
 * (createHand output - blinds posted, empty history); `actions` is the ordered, normalized action list
 * (`terminal.history.map(h => h.action)`). Deterministic and pure: same inputs → same stream, no strategy,
 * no RNG, no I/O.
 *
 * The terminal `advance()` is SPLIT into two frames (CX0 §1.3): a SWEEP frame reconstructing the pre-push
 * state (all money in the middle, `settledPot` = the full final pot) and an AWARD frame with the engine's
 * post-push state (winners' stacks include their share, `settledPot` = 0, `award` populated). The award
 * shares are recovered by diffing the terminal stacks against each seat's pre-award stack - no re-evaluation
 * of hands and no duplication of the commit/showdown logic.
 *
 * Invariants it preserves (pinned in frameStream.test.ts):
 *  - chip conservation: sum(stacks) + sum(fronts) + settledPot is constant across every frame.
 *  - stack-monotonicity: no seat's stack ever RISES except on the award frame, and a winner rises there by
 *    exactly its award share. (Otherwise a stack only ever falls - chips go out front.)
 *  - whole dollars everywhere (once the villain.ts rounding fix is in).
 */
export const frameStream = (initial: HandState, actions: readonly Action[], meta: StreamMeta): HandStream => {
  const frames: HandFrame[] = [];
  let state = initial;
  let i = 0;

  // frame 0 - the pre-first-action state (blinds already out front as fronts; no action yet).
  frames.push({
    i: i++,
    street: state.street as FrameStreet,
    phase: 'acting',
    board: [...state.board],
    settledPot: settledPotOf(state),
    toAct: state.toAct,
    action: null,
    sweep: false,
    seats: frameSeatsOf(state.players),
  });

  for (const action of actions) {
    const pre = state;
    const next = advance(pre, action);
    const fa = frameActionOf(action, pre);

    if (next.phase !== 'complete') {
      // A normal beat, OR a mid-hand street close. A street closed iff advance() rolled into the next street
      // (beginStreet dealt the board and reset the fronts). Label the sweep frame with the CLOSING street
      // (the round it concludes), matching the POC - the new board still shows because the deal is part of
      // the same beat.
      const sweep = next.street !== pre.street;
      frames.push({
        i: i++,
        street: (sweep ? pre.street : next.street) as FrameStreet,
        phase: sweep ? 'sweep' : 'acting',
        board: [...next.board],
        settledPot: settledPotOf(next),
        toAct: next.toAct,
        action: fa,
        sweep,
        seats: frameSeatsOf(next.players),
      });
      state = next;
      continue;
    }

    // ── TERMINAL: split the closing advance() into a pre-push sweep frame + a post-push award frame ──
    //
    // advance() runs resolveShowdown/awardUncontested INSIDE this same call: it adds each pot to the
    // winner's stack and empties `pots`. So `next` is already POST-award. We recover each seat's pre-award
    // stack, then its award share, without re-evaluating hands: the ONLY things that changed from `pre` to
    // `next` are (a) the closing actor's commit and (b) the pot push. Both are recoverable by arithmetic.
    const actorPre = pre.players.find((p) => p.seat === action.seat);
    if (!actorPre) throw new Error(`frameStream: no player at closing seat ${action.seat}`);
    const seatOf = (players: readonly HandState['players'][number][], seat: number) => {
      const p = players.find((pp) => pp.seat === seat);
      if (!p) throw new Error(`frameStream: no player at seat ${seat}`);
      return p;
    };
    // committedTotal is untouched by the award, so its change is PURELY the closing action's commit.
    const committedDelta = seatOf(next.players, action.seat).committedTotal - actorPre.committedTotal;
    const stackBeforeAward = (seat: number): number =>
      seat === action.seat ? actorPre.stack - committedDelta : seatOf(pre.players, seat).stack;
    const shareOf = (seat: number): number => seatOf(next.players, seat).stack - stackBeforeAward(seat);

    const potTotal = next.players.reduce((sum, p) => sum + shareOf(p.seat), 0);
    const winners = next.players.filter((p) => shareOf(p.seat) > 0).map((p) => p.seat);
    const shown = next.players
      .filter((p) => !p.folded && p.holeCards)
      .map((p) => ({ seat: p.seat, cards: p.holeCards as readonly [CardIndex, CardIndex] }));

    // (1) the sweep/close frame - the pre-push "all the money is in the middle" beat.
    frames.push({
      i: i++,
      street: pre.street as FrameStreet,
      phase: 'sweep',
      board: [...next.board],
      settledPot: potTotal,
      toAct: null,
      action: fa,
      sweep: true,
      seats: next.players.map((p) => ({
        seat: p.seat,
        stack: stackBeforeAward(p.seat),
        front: 0,
        folded: p.folded,
        allIn: p.allIn,
      })),
    });

    // (2) the award/push frame - the engine's post-push state; the pot has moved to the winner(s).
    const awardFrame: AwardFrame = {
      i: i++,
      street: pre.street as FrameStreet,
      phase: 'award',
      board: [...next.board],
      settledPot: 0,
      toAct: null,
      action: null,
      sweep: false,
      seats: next.players.map((p) => ({ seat: p.seat, stack: p.stack, front: 0, folded: p.folded, allIn: p.allIn })),
      award: { winners, amount: potTotal, shown },
    };
    frames.push(awardFrame);
    state = next;
  }

  return {
    handId: meta.handId,
    ...(meta.seed !== undefined ? { seed: meta.seed } : {}),
    stakes: {
      sb: initial.spec.stakes.sb,
      bb: initial.spec.stakes.bb,
      ...(initial.spec.stakes.ante !== undefined ? { ante: initial.spec.stakes.ante } : {}),
    },
    buttonSeat: initial.buttonSeat,
    heroSeat: meta.heroSeat,
    heroCards: meta.heroCards,
    seats: meta.seats,
    frames,
  };
};

/** Structural source for a played hand: the true initial + terminal states (as `dealSessionHand` returns).
 *  A convenience over `frameStream` for the session/continuous-table path - `terminal.history` IS the ordered
 *  action list. `seats` labels are session data (TB3); a caller with none can pass a bare seat->label list. */
export interface StreamSource {
  initial: HandState;
  terminal: HandState;
  seed?: string;
  heroSeat: number;
  heroCards: readonly [CardIndex, CardIndex];
  seats: { seat: number; label: string }[];
}

/** Build the stream from a played hand's initial + terminal states (the session path). */
export const frameStreamFrom = (src: StreamSource): HandStream =>
  frameStream(
    src.initial,
    src.terminal.history.map((h) => h.action),
    {
      handId: src.initial.handId,
      ...(src.seed !== undefined ? { seed: src.seed } : {}),
      heroSeat: src.heroSeat,
      heroCards: src.heroCards,
      seats: src.seats,
    },
  );

// ── FeltView becomes a projection of a frame (CX0 §1.4 - nothing breaks) ─────────────────────────────────

/**
 * Re-express the existing `FeltView` as a pure function of one frame. Board / pot / hero come straight from
 * the frame + stream; the opponent pods reuse the authored ring GEOMETRY (`ringFor`, do NOT rewrite it here -
 * that is CX2's even-ring job) populated with each seat's live numbers. This is the seam that lets the
 * cinematic felt (CX2) drive off frames directly while every existing per-street surface keeps working
 * (`toFeltView` / `scenarioToFelt` are untouched and stay in use).
 *
 * A "street snapshot" (what the old modes render) is just the sweep/close frame of that street; the award
 * frame projects to a $0 pot because the chips have been pushed (the felt shows the winner's stack tick up).
 */
export const projectFelt = (frame: HandFrame, stream: HandStream): FeltView => {
  const heroSeat = stream.heroSeat;
  const labelOf = (seat: number): string => stream.seats.find((s) => s.seat === seat)?.label ?? String(seat);

  const villains = frame.seats.filter((s) => s.seat !== heroSeat);
  const ring = ringFor(villains.length);
  const seats: SeatView[] = villains.map((fs, idx) => {
    const geo = ring[idx];
    const label = labelOf(fs.seat);
    return {
      ...geo,
      seat: fs.seat,
      name: label,
      avatar: label.charAt(0).toUpperCase(),
      stack: money(fs.stack),
      out: fs.folded,
      unread: false,
      active: !fs.folded && frame.toAct === fs.seat,
      bet: !fs.folded && fs.front > 0 ? money(fs.front) : '',
      button: fs.seat === stream.buttonSeat,
    };
  });

  return {
    seats,
    board: frame.board,
    pot: money(frame.settledPot),
    heroCards: stream.heroCards,
    heroLabel: labelOf(heroSeat),
    heroHasButton: stream.buttonSeat === heroSeat,
  };
};
